import React from 'react';
import {
  X,
  Calendar,
  Clock,
  MapPin,
  Users,
  Video,
  Edit,
  Trash2,
  User,
  Mail,
  CheckCircle,
  XCircle,
  AlertCircle,
  Copy,
  ExternalLink,
  MessageSquare
} from 'lucide-react';
import { OFFICE_LOCATIONS } from '../../constants/officeLocations';
import { format } from 'date-fns';
import { tr } from 'date-fns/locale';
import { CalendarEvent } from '../../hooks/useCalendar';
import { supabase } from '../../lib/supabase';

interface EventDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  event: CalendarEvent;
  onEdit: () => void;
  onDelete: (eventId: string) => void;
}

const EventDetailModal: React.FC<EventDetailModalProps> = ({
  isOpen,
  onClose,
  event,
  onEdit,
  onDelete
}) => {
  const [creatorName, setCreatorName] = React.useState<string>('Bilinmeyen');
  const [isSendingReminder, setIsSendingReminder] = React.useState(false);

  React.useEffect(() => {
    const fetchCreatorName = async () => {
      if (event.created_by) {
        // First check if creator is the current user (optimization)
        const { data: { user } } = await supabase.auth.getUser();
        if (user && user.id === event.created_by) {
          // fetch current user name from profiles just to be one source of truth or local state
          const { data } = await supabase.from('profiles').select('name').eq('id', user.id).single();
          if (data) setCreatorName(data.name);
          return;
        }

        // Fetch from profiles
        const { data } = await supabase
          .from('profiles')
          .select('name')
          .eq('id', event.created_by)
          .single();

        if (data) {
          setCreatorName(data.name);
        }
      }
    };
    fetchCreatorName();
  }, [event.created_by]);

  const handleCopyMeetingLink = () => {
    if (event.meeting_link) {
      navigator.clipboard.writeText(event.meeting_link);
      alert('Toplantı linki kopyalandı!');
    }
  };

  const handleJoinMeeting = () => {
    if (event.meeting_link) {
      window.open(event.meeting_link, '_blank');
    }
  };

  const handleSendReminder = async () => {
    if (!event.guest_phone) {
      alert('Misafir telefonu kayıtlı değil.');
      return;
    }

    if (!await window.appConfirm('Hatırlatma SMS\'i göndermek istediğinize emin misiniz?')) return;

    setIsSendingReminder(true);
    try {
      const selectedLocation = OFFICE_LOCATIONS.find(l => l.name === event.location);
      const officePhone = selectedLocation ? selectedLocation.phone : '0212 544 24 15';
      const formattedDate = new Date(event.start_time).toLocaleDateString('tr-TR');
      const formattedTime = new Date(event.start_time).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' });

      const smsMessage = `Merhabalar, Pisa Egitim'den iletisime geciyoruz. Bugun gerceklesecek olan gorusmenizi hatirlatmak isteriz.\n\nTarih: ${formattedDate}\nSaat: ${formattedTime}\nGorusmelerimiz 45 dk.'dir.\nHerhangi bir degisiklik durumunda ${officePhone} numarali hattimizdan bize bilgi vermenizi rica ederiz.\nTesekkur eder, iyi gunler dileriz.`;

      const { data: userData } = await supabase.auth.getUser();

      await supabase.functions.invoke('send-sms', {
        body: {
          phones: [event.guest_phone],
          message: smsMessage,
          userId: userData.user?.id,
          branchId: ''
        }
      });

      alert('Hatırlatma SMS\'i gönderildi.');
    } catch (error) {
      console.error('SMS Hatası:', error);
      alert('SMS gönderilirken hata oluştu.');
    } finally {
      setIsSendingReminder(false);
    }
  };

  const getResponseStatusColor = (status: string) => {
    switch (status) {
      case 'accepted': return 'text-green-600 bg-green-100 dark:bg-green-900/20 dark:text-green-400';
      case 'declined': return 'text-red-600 bg-red-100 dark:bg-red-900/20 dark:text-red-400';
      case 'tentative': return 'text-yellow-600 bg-yellow-100 dark:bg-yellow-900/20 dark:text-yellow-400';
      default: return 'text-gray-600 bg-gray-100 dark:bg-gray-900/20 dark:text-gray-400';
    }
  };

  const getResponseStatusText = (status: string) => {
    switch (status) {
      case 'accepted': return 'Kabul';
      case 'declined': return 'Red';
      case 'tentative': return 'Belki';
      default: return 'Yanıt Bekliyor';
    }
  };

  const getResponseStatusIcon = (status: string) => {
    switch (status) {
      case 'accepted': return CheckCircle;
      case 'declined': return XCircle;
      case 'tentative': return AlertCircle;
      default: return Clock;
    }
  };

  const getMeetingTypeText = (type?: string) => {
    switch (type) {
      case 'google_meet': return 'Google Meet';
      case 'zoom': return 'Zoom';
      case 'teams': return 'Microsoft Teams';
      case 'phone': return 'Telefon Görüşmesi';
      case 'in_person': return 'Yüz Yüze';
      default: return 'Online Toplantı';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'confirmed': return 'text-green-600 bg-green-100 dark:bg-green-900/20 dark:text-green-400';
      case 'tentative': return 'text-yellow-600 bg-yellow-100 dark:bg-yellow-900/20 dark:text-yellow-400';
      case 'cancelled': return 'text-red-600 bg-red-100 dark:bg-red-900/20 dark:text-red-400';
      default: return 'text-gray-600 bg-gray-100 dark:bg-gray-900/20 dark:text-gray-400';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'confirmed': return 'Onaylandı';
      case 'tentative': return 'Belirsiz';
      case 'cancelled': return 'İptal Edildi';
      default: return status;
    }
  };

  if (!isOpen || !event) return null;

  const startTime = new Date(event.start_time);
  const endTime = new Date(event.end_time);
  const duration = Math.round((endTime.getTime() - startTime.getTime()) / (1000 * 60)); // minutes

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 dark:bg-black/80 backdrop-blur-sm">
      <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl w-full max-w-4xl mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-800">
          <div className="flex items-center space-x-3">
            <div
              className="w-12 h-12 rounded-xl flex items-center justify-center"
              style={{ backgroundColor: event.calendar?.color || '#3B82F6' }}
            >
              <Calendar className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">{event.title}</h2>
              <div className="flex items-center space-x-2">
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(event.status)}`}>
                  {getStatusText(event.status)}
                </span>
                {event.calendar && (
                  <span className="text-sm text-gray-600 dark:text-gray-400">
                    {event.calendar.name}
                  </span>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={onEdit}
              className="flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors"
            >
              <Edit className="w-4 h-4" />
              <span>Düzenle</span>
            </button>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-colors"
            >
              <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Event Details */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Etkinlik Detayları</h3>

              <div className="space-y-3">
                <div className="flex items-center space-x-3">
                  <Clock className="w-5 h-5 text-gray-500" />
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Tarih ve Saat</p>
                    <p className="font-medium text-gray-900 dark:text-white">
                      {format(startTime, 'dd MMMM yyyy', { locale: tr })}
                    </p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {format(startTime, 'HH:mm', { locale: tr })} - {format(endTime, 'HH:mm', { locale: tr })} ({duration} dakika)
                    </p>
                  </div>
                </div>

                {event.location && (
                  <div className="flex items-center space-x-3">
                    <MapPin className="w-5 h-5 text-gray-500" />
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">Konum</p>
                      <p className="font-medium text-gray-900 dark:text-white">{event.location}</p>
                    </div>
                  </div>
                )}

                {event.meeting_link && (
                  <div className="flex items-center space-x-3">
                    <Video className="w-5 h-5 text-gray-500" />
                    <div className="flex-1">
                      <p className="text-sm text-gray-600 dark:text-gray-400">Online Toplantı</p>
                      <p className="font-medium text-gray-900 dark:text-white">
                        {getMeetingTypeText(event.meeting_type)}
                      </p>
                      <div className="flex items-center space-x-2 mt-2">
                        <button
                          onClick={handleJoinMeeting}
                          className="flex items-center space-x-1 bg-green-600 hover:bg-green-700 text-white px-3 py-1.5 rounded-lg text-sm transition-colors"
                        >
                          <ExternalLink className="w-3 h-3" />
                          <span>Toplantıya Katıl</span>
                        </button>
                        <button
                          onClick={handleCopyMeetingLink}
                          className="flex items-center space-x-1 bg-gray-600 hover:bg-gray-700 text-white px-3 py-1.5 rounded-lg text-sm transition-colors"
                        >
                          <Copy className="w-3 h-3" />
                          <span>Linki Kopyala</span>
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                <div className="flex items-center space-x-3">
                  <User className="w-5 h-5 text-gray-500" />
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Oluşturan</p>
                    <p className="font-medium text-gray-900 dark:text-white">
                      {creatorName}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-500">
                      {format(new Date(event.created_at), 'dd MMM yyyy HH:mm', { locale: tr })}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Katılımcılar ({((event.attendees || []).length) + (event.guest_name ? 1 : 0)})
              </h3>

              {(!event.attendees || event.attendees.length === 0) && !event.guest_name ? (
                <div className="text-center py-8">
                  <Users className="w-12 h-12 text-gray-400 dark:text-gray-500 mx-auto mb-3" />
                  <p className="text-gray-600 dark:text-gray-400">Katılımcı eklenmemiş</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {/* Guest Display */}
                  {event.guest_name && (
                    <div className="flex items-center justify-between p-3 bg-blue-50 dark:bg-blue-900/10 border border-blue-100 dark:border-blue-800 rounded-lg">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-gradient-to-br from-orange-400 to-pink-500 rounded-full flex items-center justify-center">
                          <span className="text-white text-sm font-semibold">
                            {(event.guest_name || 'M')[0]}{(event.guest_surname || '')[0]}
                          </span>
                        </div>
                        <div>
                          <p className="font-medium text-gray-900 dark:text-white">
                            {event.guest_name} {event.guest_surname}
                            <span className="ml-2 text-xs bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-400 px-2 py-0.5 rounded-full">
                              Misafir
                            </span>
                          </p>
                          <div className="flex flex-col text-sm text-gray-600 dark:text-gray-400">
                            {event.guest_email && <span>{event.guest_email}</span>}
                            {event.guest_phone && <span>{event.guest_phone}</span>}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {(event.attendees || []).map((attendee) => {
                    const StatusIcon = getResponseStatusIcon(attendee.response_status);

                    return (
                      <div key={attendee.id} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                        <div className="flex items-center space-x-3">
                          <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-500 rounded-full flex items-center justify-center">
                            <span className="text-white text-sm font-semibold">
                              {(attendee.name || 'U').split(' ').map(n => n[0]).join('')}
                            </span>
                          </div>
                          <div>
                            <p className="font-medium text-gray-900 dark:text-white">
                              {attendee.name || 'Bilinmeyen'}
                              {attendee.is_organizer && (
                                <span className="ml-2 text-xs bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400 px-2 py-0.5 rounded-full">
                                  Organizatör
                                </span>
                              )}
                            </p>
                            {attendee.email && (
                              <p className="text-sm text-gray-600 dark:text-gray-400">{attendee.email}</p>
                            )}
                          </div>
                        </div>

                        <div className="flex items-center space-x-2">
                          <span className={`inline-flex items-center space-x-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${getResponseStatusColor(attendee.response_status)}`}>
                            <StatusIcon className="w-3 h-3" />
                            <span>{getResponseStatusText(attendee.response_status)}</span>
                          </span>

                          {attendee.email && (
                            <button
                              onClick={() => window.open(`mailto:${attendee.email}`, '_blank')}
                              className="p-1.5 text-gray-500 hover:text-blue-600 dark:text-gray-400 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                              title="Email Gönder"
                            >
                              <Mail className="w-3 h-3" />
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Description */}
          {event.description && (
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">Açıklama</h3>
              <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
                <p className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                  {event.description}
                </p>
              </div>
            </div>
          )}

          {/* Meeting Link Section */}
          {event.meeting_link && (
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <Video className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                  <div>
                    <h4 className="font-medium text-blue-900 dark:text-blue-300">
                      {getMeetingTypeText(event.meeting_type)}
                    </h4>
                    <p className="text-sm text-blue-700 dark:text-blue-400">
                      Toplantıya katılmak için aşağıdaki linki kullanın
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={handleCopyMeetingLink}
                    className="flex items-center space-x-1 bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded-lg transition-colors"
                  >
                    <Copy className="w-4 h-4" />
                    <span>Kopyala</span>
                  </button>
                  <button
                    onClick={handleJoinMeeting}
                    className="flex items-center space-x-1 bg-green-600 hover:bg-green-700 text-white px-3 py-2 rounded-lg transition-colors"
                  >
                    <ExternalLink className="w-4 h-4" />
                    <span>Katıl</span>
                  </button>
                </div>
              </div>

              <div className="mt-4 p-3 bg-white dark:bg-gray-900 border border-blue-200 dark:border-blue-700 rounded-lg">
                <p className="text-sm text-gray-600 dark:text-gray-400 font-mono break-all">
                  {event.meeting_link}
                </p>
              </div>
            </div>
          )}

          {/* Event Metadata */}
          <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Etkinlik Bilgileri</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Etkinlik ID</p>
                <p className="font-medium text-gray-900 dark:text-white font-mono text-xs">{event.id}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Takvim</p>
                <div className="flex items-center space-x-2">
                  <div
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: event.calendar?.color || '#3B82F6' }}
                  />
                  <p className="font-medium text-gray-900 dark:text-white">
                    {event.calendar?.name || 'Bilinmeyen Takvim'}
                  </p>
                </div>
              </div>
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Görünürlük</p>
                <p className="font-medium text-gray-900 dark:text-white">
                  {event.visibility === 'public' ? 'Herkese Açık' :
                    event.visibility === 'private' ? 'Özel' : 'Varsayılan'}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Tüm Gün</p>
                <p className="font-medium text-gray-900 dark:text-white">
                  {event.all_day ? 'Evet' : 'Hayır'}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Oluşturulma</p>
                <p className="font-medium text-gray-900 dark:text-white">
                  {format(new Date(event.created_at), 'dd MMMM yyyy HH:mm', { locale: tr })}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Son Güncelleme</p>
                <p className="font-medium text-gray-900 dark:text-white">
                  {format(new Date(event.updated_at), 'dd MMMM yyyy HH:mm', { locale: tr })}
                </p>
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Hızlı İşlemler</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <button
                onClick={handleSendReminder}
                disabled={isSendingReminder || !event.guest_phone}
                className={`flex items-center space-x-3 p-4 rounded-xl transition-colors ${!event.guest_phone
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed dark:bg-gray-800 dark:text-gray-600'
                  : 'bg-yellow-50 hover:bg-yellow-100 dark:bg-yellow-900/20 dark:hover:bg-yellow-900/30'
                  }`}
              >
                <MessageSquare className={`w-6 h-6 ${isSendingReminder ? 'animate-pulse' : ''} text-yellow-600 dark:text-yellow-400`} />
                <div className="text-left">
                  <p className="font-medium text-yellow-900 dark:text-yellow-300">
                    {isSendingReminder ? 'Gönderiliyor...' : 'Hatırlatma Gönder'}
                  </p>
                  <p className="text-sm text-yellow-700 dark:text-yellow-400">SMS ile bilgilendir</p>
                </div>
              </button>
              {event.meeting_link && (
                <button
                  onClick={handleJoinMeeting}
                  className="flex items-center space-x-3 p-4 bg-green-50 hover:bg-green-100 dark:bg-green-900/20 dark:hover:bg-green-900/30 rounded-xl transition-colors"
                >
                  <Video className="w-6 h-6 text-green-600 dark:text-green-400" />
                  <div className="text-left">
                    <p className="font-medium text-green-900 dark:text-green-300">Toplantıya Katıl</p>
                    <p className="text-sm text-green-700 dark:text-green-400">Hemen başla</p>
                  </div>
                </button>
              )}

              <button
                onClick={onEdit}
                className="flex items-center space-x-3 p-4 bg-blue-50 hover:bg-blue-100 dark:bg-blue-900/20 dark:hover:bg-blue-900/30 rounded-xl transition-colors"
              >
                <Edit className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                <div className="text-left">
                  <p className="font-medium text-blue-900 dark:text-blue-300">Etkinliği Düzenle</p>
                  <p className="text-sm text-blue-700 dark:text-blue-400">Bilgileri güncelle</p>
                </div>
              </button>

              <button
                onClick={async () => {
                  if (await window.appConfirm('Bu etkinliği silmek istediğinize emin misiniz?')) {
                    onDelete(event.id);
                    onClose();
                  }
                }}
                className="flex items-center space-x-3 p-4 bg-red-50 hover:bg-red-100 dark:bg-red-900/20 dark:hover:bg-red-900/30 rounded-xl transition-colors"
              >
                <Trash2 className="w-6 h-6 text-red-600 dark:text-red-400" />
                <div className="text-left">
                  <p className="font-medium text-red-900 dark:text-red-300">Etkinliği Sil</p>
                  <p className="text-sm text-red-700 dark:text-red-400">Kalıcı olarak sil</p>
                </div>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EventDetailModal;
